import { numberAttr, PAGE_BREAK_MARKUP, rawAttr, type OrderedListStyle } from "@wiki/core";
import type {
  Attrs,
  MarkType,
  Node as ProseMirrorNode,
  NodeType,
  ResolvedPos,
} from "prosemirror-model";
import {
  InputRule,
  inputRules,
  textblockTypeInputRule,
  wrappingInputRule,
} from "prosemirror-inputrules";
import { sinkListItem } from "prosemirror-schema-list";
import {
  Selection,
  TextSelection,
  type Command,
  type EditorState,
  type Plugin,
  type Transaction,
} from "prosemirror-state";
import { canJoin, findWrapping } from "prosemirror-transform";

import { BULLET_LIST, TODO_LIST, orderedList, toggleList, type ListSpec } from "./commands/lists";
import { findParentOfAnyType, marks, nodes, type FoundNode } from "./schema";

const ruleOptions = { inCodeMark: false } as const;

/**
 * Apply a mark to the text captured by group 2 and drop the delimiters
 * (`editor.md` §7). The mark is removed from the stored set afterwards, so the
 * next character typed is unformatted.
 *
 * Group 1 is the marker *including* its delimiters. The rules need a guard for
 * the character in front of the opening delimiter — `**bold**` must not be read
 * as an emphasis run — and that guard is part of the match but not part of what
 * the rule may touch; measuring the rewrite from the end of the marker instead
 * of from the start of the match is what keeps the character before it (a
 * space, most often) alive.
 */
export function markInputRule(
  match: RegExp,
  markType: MarkType,
  getAttrs?: (match: RegExpMatchArray) => Attrs | null,
): InputRule {
  return new InputRule(
    match,
    (state, found, start, end) => {
      const marker = found[1];
      const captured = found[2];
      if (marker === undefined || captured === undefined || captured === "") return null;
      // `start` is where the whole match begins, guard character included; the
      // guard is the only part of the match the rule must leave alone. (The
      // range cannot be measured back from `end`: the character that completed
      // the marker has not been inserted yet when the rule runs.)
      const markerStart = start + found[0].length - marker.length;
      const textStart = markerStart + marker.indexOf(captured);
      const textEnd = textStart + captured.length;
      const tr = state.tr;
      if (textEnd < end) tr.delete(textEnd, end);
      if (textStart > markerStart) tr.delete(markerStart, textStart);
      tr.addMark(
        markerStart,
        markerStart + captured.length,
        markType.create(getAttrs?.(found) ?? null),
      );
      tr.removeStoredMark(markType);
      return tr;
    },
    ruleOptions,
  );
}

/** Replace the matched text with a node — dividers, tables, math. */
function nodeInputRule(
  match: RegExp,
  build: (found: RegExpMatchArray) => import("prosemirror-model").Node | null,
  options: { selectInside?: boolean } = {},
): InputRule {
  return new InputRule(
    match,
    (state, found, start, end) => {
      const node = build(found);
      if (!node) return null;
      // `replaceRangeWith` swallows the block the rule was typed in when the
      // rule consumed all of it, instead of leaving an empty paragraph behind.
      const tr = state.tr.replaceRangeWith(start, end, node);
      const inserted = tr.mapping.map(start, -1);
      tr.setSelection(
        TextSelection.near(
          tr.doc.resolve(
            Math.min(inserted + (options.selectInside === true ? 1 : 0), tr.doc.content.size),
          ),
        ),
      );
      return tr;
    },
    ruleOptions,
  );
}

const ALPHABET = "abcdefghijklmnopqrstuvwxyz";

export function alphaOrder(letter: string): number {
  return ALPHABET.indexOf(letter.toLowerCase()) + 1;
}

/**
 * Run `command` against the document `tr` has already produced and fold its
 * steps back into `tr`, so an input rule can delete the marker it matched and
 * then hand the rest of the work to a list command.
 */
function andThen(state: EditorState, tr: Transaction, command: Command): Transaction | null {
  const next = state.apply(tr);
  const captured: { transaction: Transaction | null } = { transaction: null };
  command(next, (commandTr) => {
    captured.transaction = commandTr;
  });
  const commandTransaction = captured.transaction;
  if (commandTransaction === null) return null;
  for (const step of commandTransaction.steps) tr.step(step);
  // Both transactions start from the same document and produce the same one,
  // so the selection the command chose is valid here too.
  tr.setSelection(Selection.fromJSON(tr.doc, commandTransaction.selection.toJSON()));
  return tr;
}

/**
 * Wrap the block the rule fired in, joining a list of the same kind directly
 * above when `joinable` says the markers belong to one run. This is what
 * `wrappingInputRule` does; it is spelled out here because the block rules need
 * to decide what to do *before* wrapping (see `blockWrapRule`).
 */
function wrapBlock(
  state: EditorState,
  start: number,
  end: number,
  type: NodeType,
  attrs: Attrs | null,
  joinable?: (node: ProseMirrorNode) => boolean,
): Transaction | null {
  const tr = state.tr.delete(start, end);
  const range = tr.doc.resolve(start).blockRange();
  const wrapping = range === null ? null : findWrapping(range, type, attrs);
  if (range === null || wrapping === null) return null;
  tr.wrap(range, wrapping);
  const before = tr.doc.resolve(start - 1).nodeBefore;
  if (
    before?.type === type &&
    canJoin(tr.doc, start - 1) &&
    (joinable === undefined || joinable(before))
  ) {
    tr.join(start - 1);
  }
  return tr;
}

/** The list wrapping a position, with the item it sits in. */
function enclosingItem($pos: ResolvedPos): { item: FoundNode; list: ProseMirrorNode } | null {
  const item = findParentOfAnyType($pos, [nodes.listItem, nodes.checkboxItem]);
  if (item === null) return null;
  return { item, list: $pos.node(item.depth - 1) };
}

/**
 * A block rule that only fires in a paragraph. A heading is a block the user
 * already chose: typing `- ` or `> ` in one must leave the text alone rather
 * than wrap the heading (`editor.md` §7 lists these as paragraph markers).
 */
function blockWrapRule(
  match: RegExp,
  build: (found: RegExpMatchArray) => { type: NodeType; attrs: Attrs | null } | null,
  joinable?: (found: RegExpMatchArray, node: ProseMirrorNode) => boolean,
): InputRule {
  return new InputRule(
    match,
    (state, found, start, end) => {
      if (state.doc.resolve(start).parent.type !== nodes.paragraph) return null;
      const target = build(found);
      if (target === null) return null;
      return wrapBlock(state, start, end, target.type, target.attrs, (node) =>
        joinable === undefined ? true : joinable(found, node),
      );
    },
    ruleOptions,
  );
}

/**
 * The list markers. Outside a list they wrap, exactly as `- ` always has.
 * Inside one they must not wrap the item a second time — the item is already a
 * list item — so they either nest (the same kind of list, one level deeper,
 * which is what `Tab` does) or retype the list they are in (a different kind,
 * which is how `- ` followed by `[] ` becomes a task list, `editor.md` §7).
 */
function listInputRule(
  match: RegExp,
  build: (found: RegExpMatchArray) => ListSpec | null,
  joinable?: (found: RegExpMatchArray, node: ProseMirrorNode) => boolean,
): InputRule {
  return new InputRule(
    match,
    (state, found, start, end) => {
      const $start = state.doc.resolve(start);
      if ($start.parent.type !== nodes.paragraph) return null;
      const spec = build(found);
      if (spec === null) return null;
      const enclosing = enclosingItem($start);
      if (enclosing === null) {
        return wrapBlock(state, start, end, spec.type, spec.attrs ?? null, (node) =>
          joinable === undefined ? true : joinable(found, node),
        );
      }
      const sameKind =
        enclosing.list.type === spec.type &&
        (rawAttr(enclosing.list, "listStyle") ?? null) === (spec.attrs?.listStyle ?? null);
      const tr = state.tr.delete(start, end);
      return andThen(
        state,
        tr,
        sameKind ? sinkListItem(enclosing.item.node.type) : toggleList(spec),
      );
    },
    ruleOptions,
  );
}

function orderedListRule(match: RegExp, listStyle: OrderedListStyle): InputRule {
  const toOrder = (value: string): number =>
    listStyle === "number" ? Number(value) : alphaOrder(value);
  return listInputRule(
    match,
    (found) => orderedList(listStyle, toOrder(found[1] ?? "1")),
    // §7: a marker joins the list above it only when it continues its sequence.
    (found, node) =>
      node.type === nodes.orderedList &&
      rawAttr(node, "listStyle") === listStyle &&
      node.childCount + (numberAttr(node, "order") ?? 1) === toOrder(found[1] ?? "1"),
  );
}

/** Every markdown input rule from `editor.md` §7, in the order it lists them. */
export function buildInputRules(): InputRule[] {
  return [
    // Blocks
    textblockTypeInputRule(/^(#{1,4})\s$/, nodes.heading, (found) => ({
      level: (found[1] ?? "#").length,
    })),
    listInputRule(/^\s*([-+*])\s$/, () => BULLET_LIST),
    orderedListRule(/^(\d+)\.\s$/, "number"),
    orderedListRule(/^([a-z])\.\s$/, "lower-alpha"),
    orderedListRule(/^([A-Z])\.\s$/, "upper-alpha"),
    // `[] ` may follow a `- ` that already became a bullet list, so inside a
    // list the rule retypes that list rather than nesting one inside it.
    listInputRule(/^\s*(?:[-+*]\s)?\[( |)]\s$/, () => TODO_LIST),
    blockWrapRule(/^\s*>\s$/, () => ({ type: nodes.blockquote, attrs: null })),
    textblockTypeInputRule(/^```([A-Za-z0-9+#-]*)[\s\n]$/, nodes.codeBlock, (found) => ({
      language: found[1] ?? "",
    })),
    // Dividers take no trailing space, and `***` is a page break.
    nodeInputRule(/^(---|___|\*\*\*)$/, (found) =>
      nodes.hr.create({ markup: found[1] === "***" ? PAGE_BREAK_MARKUP : found[1] }),
    ),
    nodeInputRule(/^\|--$/, () => {
      const cell = (type: NodeType): import("prosemirror-model").Node =>
        type.createChecked(null, nodes.paragraph.createAndFill());
      const row = (type: NodeType): import("prosemirror-model").Node =>
        nodes.tableRow.createChecked(null, [cell(type), cell(type), cell(type)]);
      return nodes.table.createChecked(null, [
        row(nodes.tableHeader),
        row(nodes.tableCell),
        row(nodes.tableCell),
      ]);
    }),
    wrappingInputRule(/^:::\s$/, nodes.notice, () => ({ style: "info" })),
    wrappingInputRule(/^\+\+\+\s$/, nodes.toggle),
    nodeInputRule(/^\$\$\$\s$/, () => nodes.mathBlock.createAndFill(), { selectInside: true }),
    // Inline
    new InputRule(
      /\$\$([^$]+)\$\$$/,
      (state, found, start, end) => {
        const content = found[1];
        if (content === undefined) return null;
        const node = nodes.mathInline.create(null, nodes.text.schema.text(content));
        return state.tr.replaceWith(start, end, node);
      },
      ruleOptions,
    ),
    markInputRule(/(?:^|[^*])(\*\*([^*]+)\*\*)$/, marks.strong),
    markInputRule(/(?:^|[^*])(\*([^*]+)\*)$/, marks.em),
    markInputRule(/(?:^|[^~])(~~([^~]+)~~)$/, marks.strikethrough),
    markInputRule(/(?:^|[^`])(`([^`]+)`)$/, marks.code),
    markInputRule(/(?:^|[^=])(==([^=]+)==)$/, marks.highlight),
    markInputRule(/(?:^|[^!])(!!([^!]+)!!)$/, marks.placeholder),
    new InputRule(
      /\[([^\]]+)]\(([^)\s]+)\)$/,
      (state, found, start, end) => {
        const [, text, href] = found;
        if (text === undefined || href === undefined) return null;
        const node = nodes.text.schema.text(text, [marks.link.create({ href })]);
        return state.tr.replaceWith(start, end, node).removeStoredMark(marks.link);
      },
      ruleOptions,
    ),
  ];
}

export function inputRulesPlugin(extra: InputRule[] = []): Plugin {
  return inputRules({ rules: [...buildInputRules(), ...extra] });
}
